import { Lock, Shield, Users } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import notify from '@/lib/notify';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/Common/badge';
import { Switch } from '@/components/Common/switch';
import documentService, { type DocumentVisibility } from '@/services/documentService';
import { useEffectiveUser, useIsOrganizationAdmin } from '@/stores/authStore';
import { apiErrorDetail, canManageSharing } from '@/lib/knowledgeSharing';

const BADGE_CLASS = 'gap-1 px-2 py-0 text-[11px] font-medium flex-shrink-0';

export const PrivateBadge: React.FC<{ tooltip: string; className?: string }> = ({ tooltip, className }) => {
  const { t } = useTranslation();
  return (
    <Badge
      variant="outline"
      title={tooltip}
      aria-label={`${t('knowledgeSharing.badges.private')}: ${tooltip}`}
      className={cn(BADGE_CLASS, 'border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-300', className)}
    >
      <Lock className="h-3 w-3" aria-hidden="true" />
      {t('knowledgeSharing.badges.private')}
    </Badge>
  );
};

export const SharedBadge: React.FC<{ tooltip: string; className?: string }> = ({ tooltip, className }) => {
  const { t } = useTranslation();
  return (
    <Badge
      variant="outline"
      title={tooltip}
      className={cn(BADGE_CLASS, 'border-emerald-300 text-emerald-700 dark:border-emerald-800 dark:text-emerald-300', className)}
    >
      <Users className="h-3 w-3" aria-hidden="true" />
      {t('knowledgeSharing.badges.shared')}
    </Badge>
  );
};

export const DefaultFolderBadge: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  return (
    <Badge
      variant="outline"
      title={t('knowledgeSharing.badges.defaultTooltip')}
      className={cn(BADGE_CLASS, 'border-blue-300 text-blue-700 dark:border-blue-800 dark:text-blue-300', className)}
    >
      <Shield className="h-3 w-3" aria-hidden="true" />
      {t('knowledgeSharing.badges.default')}
    </Badge>
  );
};

export interface DocumentVisibilityControlProps {
  documentId: string;
  /** Missing on records from before lot K: those are shared. */
  visibility?: DocumentVisibility;
  ownerId?: string | null;
  /** Show the indicator only, even to the owner or an admin. */
  readOnly?: boolean;
  /** Called optimistically, then with the server value (or the previous one on failure). */
  onVisibilityChange: (documentId: string, visibility: DocumentVisibility) => void;
  className?: string;
}

/**
 * "Share with the organization" switch of a domain, use case or solution document,
 * for its owner or an admin; a read-only "Private" badge for everyone else.
 * Not for custom folder documents: those follow their folder.
 */
export const DocumentVisibilityControl: React.FC<DocumentVisibilityControlProps> = ({
  documentId,
  visibility = 'org',
  ownerId,
  readOnly = false,
  onVisibilityChange,
  className,
}) => {
  const { t } = useTranslation();
  const user = useEffectiveUser();
  const isOrganizationAdmin = useIsOrganizationAdmin();
  const [saving, setSaving] = useState(false);

  const isPrivate = visibility === 'private';
  const canEdit = !readOnly && canManageSharing(user, isOrganizationAdmin, ownerId);
  const privateBadge = isPrivate ? <PrivateBadge tooltip={t('knowledgeSharing.badges.privateDocumentTooltip')} /> : null;

  if (!canEdit) {
    return privateBadge ? <div className={cn('flex items-center', className)}>{privateBadge}</div> : null;
  }

  const handleChange = async (shared: boolean) => {
    const previous = visibility;
    const next: DocumentVisibility = shared ? 'org' : 'private';
    onVisibilityChange(documentId, next);
    setSaving(true);
    try {
      const updated = await documentService.setVisibility(documentId, next);
      const confirmed = updated.visibility ?? next;
      onVisibilityChange(documentId, confirmed);
      notify.success(
        confirmed === 'org' ? t('knowledgeSharing.document.shared') : t('knowledgeSharing.document.madePrivate')
      );
    } catch (error) {
      console.error('Error changing document visibility:', error);
      onVisibilityChange(documentId, previous);
      notify.error(apiErrorDetail(error) ?? t('knowledgeSharing.errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      onClick={(event) => event.stopPropagation()}
    >
      <label
        className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer"
        title={t('knowledgeSharing.document.shareTooltip')}
      >
        <Switch
          checked={!isPrivate}
          onCheckedChange={(checked) => void handleChange(checked)}
          disabled={saving}
          aria-busy={saving}
        />
        <span>{t('knowledgeSharing.document.shareWithOrg')}</span>
      </label>
      {privateBadge}
    </div>
  );
};

export default DocumentVisibilityControl;
